import {
  FormattingRule,
  ComponentOrReferenceFormattingRule,
} from '@ardoq/data-model';
import {
  QueryBuilderFilterType,
  RuleNotificationType,
} from '@ardoq/query-builder';
import {
  type FormattingGroup,
  getFilterGroups,
  isLegacyFormattingRule,
  isRuleVersionSupported,
  isTypeRule,
  isValidTypeRule,
} from '@ardoq/perspectives';
import {
  APIEntityType,
  type FieldGlobalAttributes,
  FormattingRuleOperator,
} from '@ardoq/api-types';
import { getCustomFieldFormatting } from './getFormattingForInvalidRuleByRuleId';
import { isComponentOrReferenceFormattingRule } from '@ardoq/perspectives';
import { Locale } from '@ardoq/locale';
import { type I18nLocale, t } from '@ardoq/i18n';

type GetFormattingRuleNotificationParamsFunctionArgs = {
  componentsFormattingGroups: FormattingGroup[];
  referencesFormattingGroups: FormattingGroup[];
  getFormattingForInvalidRule?: (ruleId: string) => any;
  isNewConditionalFormatting: boolean;
  datasetComponentTypeNamesSet?: Set<string>;
  datasetReferenceTypeNamesSet?: Set<string>;
  i18nLocale: I18nLocale;
};

type FindFormattingByFieldArgs = {
  field: string;
  formattingGroups: FormattingGroup[];
};

const findFormattingByField = ({
  field,
  formattingGroups,
}: FindFormattingByFieldArgs) => {
  const formatting = formattingGroups.flatMap(({ filters }) => filters);
  return formatting.find(({ id }) => field === id) || null;
};

export const getFormattingRuleNotificationParamsFunction =
  ({
    componentsFormattingGroups,
    referencesFormattingGroups,
    getFormattingForInvalidRule,
    isNewConditionalFormatting,
    datasetComponentTypeNamesSet,
    datasetReferenceTypeNamesSet,
    i18nLocale,
  }: GetFormattingRuleNotificationParamsFunctionArgs) =>
  (rule: FormattingRule) => {
    if (rule.type === APIEntityType.TAG) {
      return;
    }

    const componentOrReferenceRule = rule as ComponentOrReferenceFormattingRule;
    const { typeNames } = componentOrReferenceRule;
    const typeNamesSet =
      rule.type === APIEntityType.COMPONENT
        ? datasetComponentTypeNamesSet
        : datasetReferenceTypeNamesSet;

    if (typeNames && typeNames.length > 0 && typeNamesSet) {
      const missingTypes = typeNames.filter(name => !typeNamesSet.has(name));

      if (missingTypes.length === typeNames.length) {
        return {
          message: (
            <>
              {t(
                i18nLocale,
                'perspectiveSidebar:formattingTab.ruleNotApplicable',
                `This conditional format can't be applied to the opened datasets`
              )}
            </>
          ),
          notificationType: RuleNotificationType.WARNING,
        };
      }

      if (missingTypes.length > 0) {
        return {
          message: (
            <>
              {t(
                i18nLocale,
                'perspectiveSidebar:formattingTab.someTypesDeleted',
                `One or more selected types are no longer in the dataset`
              )}
            </>
          ),
          notificationType: RuleNotificationType.WARNING,
        };
      }
    }

    if (!componentOrReferenceRule.field) {
      return;
    }
    const filterGroups = getFilterGroups({
      i18nLocale,
      filterGroups:
        rule.type === APIEntityType.COMPONENT
          ? componentsFormattingGroups
          : referencesFormattingGroups,
      invalidFormattingRuleFilter: getFormattingForInvalidRule?.(rule.__id),
      typeNames,
      isNewConditionalFormatting,
    });

    const formatting = findFormattingByField({
      field: componentOrReferenceRule.field,
      formattingGroups: filterGroups,
    });
    if (!formatting) {
      const message = t(
        i18nLocale,
        'perspectiveSidebar:formattingTab.fieldDeleted',
        { fieldName: rule.field },
        `The {{fieldName}} field used for this conditional format has been deleted`
      );
      return {
        message: <>{message}</>,
        notificationType: RuleNotificationType.WARNING,
      };
    }

    return;
  };

type TypeNamesSets = {
  componentTypeNamesSet: Set<string>;
  referenceTypeNamesSet: Set<string>;
};
export const filterValidConditionalFormattingRules = (
  rules: FormattingRule[],
  { componentTypeNamesSet, referenceTypeNamesSet }: TypeNamesSets
): FormattingRule[] => {
  return rules.filter(
    rule =>
      isLegacyFormattingRule(rule) ||
      !isTypeRule(rule) ||
      isValidTypeRule(rule, {
        componentTypeNamesSet,
        referenceTypeNamesSet,
      })
  );
};

/** @deprecated Remove when BE has migrated all rules to v1 */
export const ensureCollapsedForUnsupportedRules = (
  rules: FormattingRule[]
): FormattingRule[] => {
  return rules.map(rule => {
    const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

    if (isRuleUnsupported && rule.isCollapsed === undefined) {
      return {
        ...rule,
        isCollapsed: true,
      };
    }

    return rule;
  });
};

/** @deprecated Remove when BE has migrated all rules to v1 */
export const normalizeLegacyCheckboxComparator = (
  rules: FormattingRule[],
  fieldDataByRuleId: Record<string, FieldGlobalAttributes | null>,
  locale: Locale
): FormattingRule[] => {
  return rules.map(rule => {
    if (!isComponentOrReferenceFormattingRule(rule)) return rule;
    const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);
    const ruleFormatting = getCustomFieldFormatting(
      rule,
      fieldDataByRuleId[rule.__id] ?? null,
      locale
    );

    if (
      isRuleUnsupported &&
      ruleFormatting?.filterType === QueryBuilderFilterType.BOOLEAN &&
      (rule.value === false || rule.value === null)
    ) {
      return {
        ...rule,
        comparator: FormattingRuleOperator.NOT_BOOLEAN,
      };
    }

    return rule;
  });
};
